/**
 * Sidebar v2 drag sorting strategy: the transform projection that previews
 * the committed layout while a row is lifted.
 *
 * Split out of sidebarV2Drag.ts (same origin and adaptations) for the
 * file-size ratchet; the collision detector and axis modifiers stay there.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.drag.ts` at
 * pin e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: the projection closures are decomposed
 * into named helpers.
 */
import { verticalListSortingStrategy, type SortingStrategy } from "@dnd-kit/sortable";

import {
  resolveSidebarDropTarget,
  sidebarListItemId,
  type SidebarListMarker,
  type SidebarListItem,
  type SidebarSection,
} from "./sidebarV2List";

import type { DragTransform } from "./sidebarV2Drag";

// ── Sorting strategy ─────────────────────────────────────────────────

interface StrategyInput {
  readonly items: readonly SidebarListItem[];
  readonly settledOrder: readonly string[];
  readonly settledExpanded: boolean;
  readonly settledVisibleCount?: number;
  readonly routeThreadKey?: string | null;
  readonly snoozedThreadCount?: number;
  readonly cardHeight?: number;
  readonly slimHeight?: number;
  readonly boundaryLabelHeight?: number;
}

const stationary = { x: 0, y: 0, scaleX: 1, scaleY: 1 };
const hidden = { ...stationary, scaleY: 0 };
type ThreadItem = Extract<SidebarListItem, { kind: "thread" }>;
type Layout = Parameters<SortingStrategy>[0];
type Rect = {
  top: number;
  height: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
};

/** Group the non-lifted thread items by section, harvesting the measured
 * card/slim/header heights along the way. Cards are 4.875rem + padding and
 * slim rows and placeholders are h-9, scaled by the measured root scale. */
// fallow-ignore-next-line complexity -- byte-faithful port of upstream Sidebar.drag.ts / threadSort.ts at pin e3e7cc3f (#3601 precedent)
function groupMeasuredItems(
  items: readonly SidebarListItem[],
  rects: readonly Rect[],
  activeKey: string,
): {
  readonly groups: Record<SidebarSection, ThreadItem[]>;
  readonly cardHeight: number | undefined;
  readonly slimHeight: number | undefined;
  readonly headerScale: number | undefined;
} {
  const groups: Record<SidebarSection, ThreadItem[]> = {
    pinned: [],
    active: [],
    snoozed: [],
    settled: [],
  };
  let cardHeight: number | undefined;
  let slimHeight: number | undefined;
  let headerScale: number | undefined;
  for (const [index, item] of items.entries()) {
    if (item.kind === "marker") {
      if (item.marker === "settled-header" || item.marker === "snoozed-header") {
        const height = rects[index]?.height;
        if (height) headerScale ??= height / 32;
      }
      continue;
    }
    if (item.section === "pinned" || item.section === "active") cardHeight ??= rects[index]?.height;
    else slimHeight ??= rects[index]?.height;
    if (item.key !== activeKey) groups[item.section].push(item);
  }
  return { groups, cardHeight, slimHeight, headerScale };
}

/** Insert the lifted thread into its target group at the position the drop
 * order ranks it at. */
function insertActiveAtRank(
  group: ThreadItem[],
  order: readonly string[],
  active: ThreadItem,
  section: SidebarSection,
): void {
  const ranks = new Map(order.map((key, index) => [key, index]));
  const rank = ranks.get(active.key) ?? Number.POSITIVE_INFINITY;
  const index = group.findIndex((item) => (ranks.get(item.key) ?? Number.POSITIVE_INFINITY) > rank);
  group.splice(index < 0 ? group.length : index, 0, { ...active, section });
}

/** The committed visible settled order: the canonical order minus the lifted
 * row (unless it settles), paged to the visible count and route-pinned. */
function projectedSettledItems(
  input: StrategyInput,
  groups: Record<SidebarSection, ThreadItem[]>,
  activeKey: string,
  settlesIntoSettled: boolean,
): ThreadItem[] {
  const canonical =
    input.settledOrder.length > 0 ? input.settledOrder : groups.settled.map((item) => item.key);
  const settledOrder = canonical.filter((key) => key !== activeKey || settlesIntoSettled);
  const visible = input.settledExpanded
    ? settledOrder.slice(0, input.settledVisibleCount ?? settledOrder.length)
    : [];
  const routeKey = input.routeThreadKey;
  if (routeKey && settledOrder.includes(routeKey) && !visible.includes(routeKey)) {
    visible.push(routeKey);
  }
  return visible.map((key) => ({ kind: "thread" as const, key, section: "settled" as const }));
}

/** The projected list: pinned block, inbox, snoozed shelf, settled shelf —
 * placeholders standing in for empty sections. */
function assembleProjectedItems(
  groups: Record<SidebarSection, ThreadItem[]>,
  input: StrategyInput,
  items: readonly SidebarListItem[],
  active: ThreadItem,
): SidebarListItem[] {
  const projected: SidebarListItem[] = [];
  /** Append a section marker to the projected drag layout. */
  const marker = (name: SidebarListMarker) => projected.push({ kind: "marker", marker: name });
  /** Append projected section rows, substituting a placeholder when the section is empty. */
  const section = (name: "active" | "settled") => {
    if (groups[name].length > 0) projected.push(...groups[name]);
    else marker(`${name}-placeholder`);
  };
  marker("pinned-header");
  projected.push(...groups.pinned);
  marker("pinned-divider");
  section("active");
  const shelfRendered = items.some(
    (item) => item.kind === "marker" && item.marker === "snoozed-header",
  );
  if (
    groups.snoozed.length > 0 ||
    ((active.section !== "snoozed" || (input.snoozedThreadCount ?? 0) > 1) && shelfRendered)
  ) {
    marker("snoozed-header");
    projected.push(...groups.snoozed);
  }
  marker("settled-header");
  section("settled");
  return projected;
}

/** Per-item rendered heights for the projection: drag-label boundaries and
 * placeholders use fixed space, the lifted row its fallback, peers their
 * measured rects. */
function projectedHeights(
  projected: readonly SidebarListItem[],
  indices: ReadonlyMap<string, number>,
  rects: readonly Rect[],
  activeKey: string,
  cardHeight: number,
  slimHeight: number,
  labelHeight: number,
): number[] {
  // fallow-ignore-next-line complexity -- decomposed from the byte-faithful upstream projection (pin e3e7cc3f, #3601 precedent)
  return projected.map((item) =>
    projectedItemHeight(item, indices, rects, activeKey, cardHeight, slimHeight, labelHeight),
  );
}

/** Marker height: drag labels open fixed space and placeholders are h-9.
 * Null for shelf headers, which keep their measured rects like rows. */
function projectedMarkerHeight(
  marker: SidebarListMarker,
  labelHeight: number,
  slimHeight: number,
): number | null {
  if (marker === "pinned-header" || marker === "pinned-divider") return labelHeight;
  if (marker.endsWith("placeholder")) return slimHeight;
  return null;
}

/** One projected item's rendered height. */
// fallow-ignore-next-line complexity -- decomposed from the byte-faithful upstream projection and reorder planner (pin e3e7cc3f, #3601 precedent)
function projectedItemHeight(
  item: SidebarListItem,
  indices: ReadonlyMap<string, number>,
  rects: readonly Rect[],
  activeKey: string,
  cardHeight: number,
  slimHeight: number,
  labelHeight: number,
): number {
  const index = indices.get(sidebarListItemId(item));
  const rect = index === undefined ? undefined : rects[index];
  if (item.kind === "marker") {
    return (
      projectedMarkerHeight(item.marker, labelHeight, slimHeight) ?? rect?.height ?? slimHeight
    );
  }
  const fallback = item.section === "pinned" || item.section === "active" ? cardHeight : slimHeight;
  return item.key === activeKey ? fallback : (rect?.height ?? fallback);
}

/** Consume the shelf's auto margin as drag labels and resized rows need
 * room, keeping the combined shelves at their measured bottom. */
function initialShelfSpace(
  items: readonly SidebarListItem[],
  rects: readonly Rect[],
  heights: readonly number[],
): number {
  const firstRect = rects[0];
  if (firstRect === undefined) return 0;
  const firstShelf = items.findIndex(
    (item) =>
      item.kind === "marker" &&
      (item.marker === "snoozed-header" || item.marker === "settled-header"),
  );
  const shelfRect = rects[firstShelf];
  const beforeShelf = rects[firstShelf - 1];
  const lastRect = rects.at(-1);
  if (!(shelfRect && beforeShelf && lastRect && shelfRect.top > beforeShelf.bottom + 1)) {
    return 0;
  }
  return Math.max(
    0,
    lastRect.bottom - firstRect.top - heights.reduce((sum, height) => sum + height + 1, -1),
  );
}

/** Walk the projected items top-down, assigning each rendered item its
 * committed top offset. */
function walkProjectedTops(
  projected: readonly SidebarListItem[],
  items: readonly SidebarListItem[],
  rects: readonly Rect[],
  indices: ReadonlyMap<string, number>,
  heights: readonly number[],
  activeIndex: number,
): DragTransform[] {
  const result: DragTransform[] = items.map(() => hidden);
  const firstRect = rects[0];
  if (firstRect === undefined) return result;
  let top = firstRect.top;
  let shelfSpace = initialShelfSpace(items, rects, heights);
  for (const [projectedIndex, item] of projected.entries()) {
    if (
      item.kind === "marker" &&
      (item.marker === "snoozed-header" || item.marker === "settled-header")
    ) {
      top += shelfSpace;
      shelfSpace = 0;
    }
    const index = indices.get(sidebarListItemId(item));
    const rect = index === undefined ? undefined : rects[index];
    if (index !== undefined && rect) result[index] = { ...stationary, y: top - rect.top };
    top += heights[projectedIndex]! + 1;
  }
  result[activeIndex] = stationary;
  return result;
}

/** Preview the committed section layout without moving or mounting DOM nodes.
 * A zero scaleY marks rows/markers to hide while retaining their measured nodes. */
export function createSidebarSortingStrategy(input: {
  items: readonly SidebarListItem[];
  settledOrder: readonly string[];
  settledExpanded: boolean;
  settledVisibleCount?: number;
  routeThreadKey?: string | null;
  snoozedThreadCount?: number;
  cardHeight?: number;
  slimHeight?: number;
  /** Space each pinned boundary opens for its label while dragging. The
   * markers stay zero height at rest, so nothing is reserved until pickup. */
  boundaryLabelHeight?: number;
}): SortingStrategy {
  const { items } = input;
  const indices = new Map(items.map((item, index) => [sidebarListItemId(item), index]));
  let previous: Pick<Layout, "rects" | "activeIndex" | "overIndex"> | undefined;
  let transforms: ReturnType<SortingStrategy>[] | null = [];

  /** Project per-row drag transforms for the destination shelf using measured row and marker heights. */
  // fallow-ignore-next-line complexity -- byte-faithful upstream projection, decomposed
  function project({ rects, activeIndex, overIndex }: Layout): DragTransform[] {
    const active = items[activeIndex];
    const over = items[overIndex] ?? active;
    if (!active || active.kind !== "thread" || !over || !rects[0]) return [];
    const target = resolveSidebarDropTarget(items, active.key, sidebarListItemId(over));
    if (!target) return [];
    const { groups, cardHeight, slimHeight, headerScale } = groupMeasuredItems(
      items,
      rects,
      active.key,
    );
    const scale =
      slimHeight !== undefined ? slimHeight / 36 : (headerScale ?? (cardHeight ?? 82) / 82);
    const resolvedCard = cardHeight ?? 82 * scale;
    const resolvedSlim = slimHeight ?? 36 * scale;
    const order =
      target.section === "pinned"
        ? target.pinnedOrder
        : target.section === "settled"
          ? input.settledOrder
          : target.activeOrder;
    insertActiveAtRank(groups[target.section], order, active, target.section);
    groups.settled = projectedSettledItems(input, groups, active.key, target.section === "settled");
    const projected = assembleProjectedItems(groups, input, items, active);
    const heights = projectedHeights(
      projected,
      indices,
      rects,
      active.key,
      resolvedCard,
      resolvedSlim,
      (input.boundaryLabelHeight ?? 0) * scale,
    );
    return walkProjectedTops(projected, items, rects, indices, heights, activeIndex);
  }

  return (args) => {
    if (
      previous?.rects !== args.rects ||
      previous.activeIndex !== args.activeIndex ||
      previous.overIndex !== args.overIndex
    ) {
      previous = args;
      transforms = project(args);
    }
    return transforms === null
      ? verticalListSortingStrategy(args)
      : (transforms[args.index] ?? stationary);
  };
}
